import {Button, Input, Title} from '@priority-software/priority-style-react';
import React, {useCallback, useState} from 'react';
import {useEmailContext} from '../../../../../context';
import {texts} from '../../../../../texts';
import {hyperlinkType} from '../../../constants';
import styles from '../../../../styles.module.scss';
import {useEventCallback} from 'usehooks-ts';
import {useUpdateActiveHyperlink} from './useUpdateActiveHyperlink';
import {THyperlinkLocalStates, TInputTypes} from './types';
import {useUpdateHyperlinkState} from './useUpdateHyperlinkState';
import {createDataTestId} from '../../../../../../../utils/dataTestId';

export const useHyperlinkCmp = () => {
    const {
        states: {hyperlinkState},
        handlers: {updateHyperlinkButton}
    } = useEmailContext();

    useUpdateActiveHyperlink();

    const [hyperlinkStates, setHyperlinkStates] = useState<THyperlinkLocalStates>({
        text: hyperlinkState?.text || '',
        url: ''
    });

    useUpdateHyperlinkState({hyperlinkStates, setHyperlinkStates});

    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>, type: TInputTypes = hyperlinkType.TEXT) => {
            setHyperlinkStates(prev => ({
                ...prev,
                [type]: e.target.value
            }));
        },
        []
    );

    const resetStates = useCallback(() => {
        setHyperlinkStates({text: '', url: ''});
    }, []);

    const handleUpdate = useEventCallback(() => {
        updateHyperlinkButton({...hyperlinkStates, active: true});
        resetStates();
    });

    const renderHeader = () => (
        <Title
            componentClasses={styles.fullWidth}
            skin="h4"
            {...createDataTestId(`email-hyperlink-title`, true)}
        >
            {texts.add_hyperlink_title}
        </Title>
    );

    const renderBody = () => (
        <>
            <Input
                {...createDataTestId(`email-hyperlink-input-show`, true)}
                placeholder={texts.display_text_placeholder}
                onChange={onChange}
                label={texts.display_text}
                value={hyperlinkStates.text}
                size="large"
            />
            <Input
                {...createDataTestId(`email-hyperlink-input-link`, true)}
                placeholder={texts.url_text_placeholder}
                onChange={e => onChange(e, hyperlinkType.URL)}
                label={texts.url_text}
                value={hyperlinkStates.url}
                size="large"
            />
        </>
    );

    const renderFooter = () => (
        <Button
            disabled={!(hyperlinkStates.text && hyperlinkStates.url)}
            size={'large'}
            width="fullWidth"
            onClick={handleUpdate}
            {...createDataTestId(`email-hyperlink-footer-button`, true)}
        >
            {texts.add}
        </Button>
    );

    return {renderHeader, renderBody, renderFooter, onChange} as const;
};
